import { useState } from "react";
import { PageBanner } from "@/components/ui/PageBanner";
import { IMG } from "@/lib/assets";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { apiErr, BACKEND_URL } from "@/lib/api";
import { GenomePanel } from "@/components/ui/GenomePanel";
import { SleepReport } from "@/components/ui/SleepReport";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Users, Moon, Gauge, Activity, Radio } from "lucide-react";

export default function Clients() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState(null);
  const [sleepReport, setSleepReport] = useState(null);
  const [sleepOpen, setSleepOpen] = useState(false);

  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: async () => (await api.get("/api/clients")).data });
  const { data: workouts = [] } = useQuery({ queryKey: ["workouts"], queryFn: async () => (await api.get("/api/workouts")).data });
  const templates = workouts.filter((w) => !w.assigned_to);

  const { data: genome } = useQuery({
    queryKey: ["genome", selected?.user_id],
    queryFn: async () => (await api.get("/api/ava/genome", { params: { client_id: selected.user_id } })).data,
    enabled: !!selected,
  });

  const assign = async (workoutId) => {
    try {
      await api.post(`/api/workouts/${workoutId}/assign`, { client_id: selected.user_id });
      toast.success("Workout assigned");
    } catch (err) { toast.error(apiErr(err)); }
  };

  const runSleep = async () => {
    try {
      const { data } = await api.post("/api/ava/sleep", null, { params: { client_id: selected.user_id } });
      setSleepReport(data); setSleepOpen(true);
      qc.invalidateQueries({ queryKey: ["genome", selected.user_id] });
      qc.invalidateQueries({ queryKey: ["clients"] });
      toast.success(`Sleep cycle #${data.cycle} complete`);
      playNarration(data.narration);
    } catch (err) { toast.error(apiErr(err)); }
  };

  const playNarration = async (text) => {
    if (!text) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/ava/narrate`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text }),
      });
      if (!res.ok) throw new Error("tts");
      new Audio(URL.createObjectURL(await res.blob())).play();
    } catch (_) {
      if ("speechSynthesis" in window) window.speechSynthesis.speak(new SpeechSynthesisUtterance(text));
    }
  };

  return (
    <div className="p-4 md:p-8 space-y-6">
      <PageBanner image={IMG.progress} height="h-56 md:h-64" overline="roster" title="Customers"
        subtitle="Each member has their own OCTON coaching profile. Inspect it, assign workouts and run their overnight reviews." />

      {/* Fleet genome pulse - every compartment side by side */}
      <div className="surface rounded-lg p-5">
        <div className="flex items-center gap-2 mb-4">
          <Radio className="w-4 h-4 text-acid" />
          <span className="font-heading font-bold tracking-tight">Fleet Genome Pulse</span>
          <span className="mono-label ml-auto">{clients.length} compartments</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
          {clients.map((c) => {
            const g = c.genome;
            const pe = g?.prediction_error ?? 0.5;
            const hot = pe > 0.55;
            return (
              <button key={c.user_id} onClick={() => setSelected(c)} data-testid={`fleet-${c.user_id}`}
                className={`text-left rounded-md p-3 border transition-colors ${
                  selected?.user_id === c.user_id ? "border-acid bg-acid/5" : "border-[#1E222A] surface-elev hover:border-[#343A4A]"}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium truncate">{c.name}</span>
                  <span className="mono-label" style={{ color: hot ? "#FF3B30" : "#CCFF00" }}>{pe.toFixed(2)}</span>
                </div>
                <div className="h-1.5 bg-[#05060a] rounded-full overflow-hidden mb-2">
                  <motion.div className="h-full rounded-full" style={{ background: hot ? "#FF3B30" : "#CCFF00" }}
                    initial={{ width: 0 }} animate={{ width: `${pe * 100}%` }} transition={{ duration: 0.7 }} />
                </div>
                <div className="flex items-center justify-between mono-label">
                  <span className="flex items-center gap-1"><Activity className="w-3 h-3" />{g?.cycles ?? 0}c</span>
                  <span>{g?.concept_count ?? 0} concepts</span>
                </div>
                <p className="mono-label mt-1 text-acid truncate">{g?.memory_state || "forming"}</p>
              </button>
            );
          })}
          {clients.length === 0 && <p className="text-[#565C6F] text-sm">No clients yet.</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4 space-y-2">
          {clients.map((c, i) => (
            <motion.button key={c.user_id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}
              onClick={() => setSelected(c)} data-testid={`client-${i}`}
              className={`w-full text-left surface rounded-lg p-4 flex items-center gap-3 transition-colors ${
                selected?.user_id === c.user_id ? "border-acid" : "hover:border-[#343A4A]"}`}>
              <div className="w-10 h-10 rounded-full bg-neural/20 border border-neural/40 flex items-center justify-center font-bold">
                {c.name?.[0]?.toUpperCase() || "?"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{c.name}</p>
                <p className="text-xs text-[#8A91A6] truncate">{c.email}</p>
              </div>
              {c.genome && (
                <span className="mono-label flex items-center gap-1" style={{ color: c.genome.prediction_error > 0.55 ? "#FF3B30" : "#CCFF00" }}>
                  <Gauge className="w-3 h-3" />{c.genome.prediction_error}
                </span>
              )}
            </motion.button>
          ))}
          {clients.length === 0 && <p className="text-[#565C6F] text-sm">No clients yet.</p>}
        </div>

        <div className="lg:col-span-8">
          {!selected ? (
            <div className="surface rounded-lg p-12 h-full flex flex-col items-center justify-center text-center">
              <Users className="w-8 h-8 text-[#565C6F] mb-3" />
              <p className="text-[#8A91A6]">Select a client to inspect their HNHB compartment.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="surface rounded-lg p-5 flex flex-wrap items-center gap-3 justify-between">
                <div>
                  <h2 className="font-heading font-bold text-xl tracking-tight">{selected.name}</h2>
                  <p className="mono-label mt-1">{selected.membership_status} · {selected.membership_plan || "no plan"}</p>
                </div>
                <div className="flex gap-2 items-center">
                  <Select onValueChange={assign}>
                    <SelectTrigger className="w-48 bg-[#0F1115] border-[#1E222A] h-10" data-testid="assign-select"><SelectValue placeholder="Assign workout…" /></SelectTrigger>
                    <SelectContent className="surface border-[#1E222A]">
                      {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button onClick={runSleep} className="bg-[#FFB340] text-black hover:bg-[#ffc35f] rounded-full h-10" data-testid="client-sleep-btn">
                    <Moon className="w-4 h-4" /> Sleep cycle
                  </Button>
                </div>
              </div>
              <GenomePanel genome={genome} />
            </div>
          )}
        </div>
      </div>
      <SleepReport open={sleepOpen} onOpenChange={setSleepOpen} report={sleepReport} onNarrate={() => playNarration(sleepReport?.narration)} />
    </div>
  );
}
